import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  ContactTokenStatusDto,
  InviteCollaboratorInput,
  InviteCollaboratorResultDto,
  RevokeCollaboratorInput,
  SupplierTeamClient,
  SupplierTeamDto,
} from '@/api/supplier-team-client';
import { useSupplierTeamClient } from '@/api/supplier-team-client-context';

// =============================================================================
// use-supplier-team.ts — Hooks TanStack Query de la page « Mon équipe » (§3.7, F4).
// =============================================================================
// Lecture (useQuery) + mutations (useMutation invite/revoke). Le client effectif vient du
// SupplierTeamClientProvider (doublable). ZÉRO règle métier : l'autorité (session/withTenant, ACL
// MASTER-only, mur légal domaine, garde [F1], invariant n°1) est l'API. Après une mutation réussie,
// on invalide l'annuaire pour refléter l'état serveur (aucune donnée métier en localStorage,
// RULE_REF_006).

const SUPPLIER_TEAM_KEY = ['supplier-team'] as const;

/** Clé de query de l'annuaire équipe fournisseur. */
export function supplierTeamKey(): readonly unknown[] {
  return [...SUPPLIER_TEAM_KEY];
}

/** GET annuaire des contacts du SIRET du MASTER en session (MASTER + collaborateurs). */
export function useSupplierTeam(): UseQueryResult<SupplierTeamDto, Error> {
  const client: SupplierTeamClient = useSupplierTeamClient();
  return useQuery<SupplierTeamDto, Error>({
    queryKey: supplierTeamKey(),
    queryFn: () => client.listTeam(),
  });
}

/** Clé de query du statut du lien d'activation des contacts du SIRET. */
export function supplierTeamTokenStatusKey(): readonly unknown[] {
  return [...SUPPLIER_TEAM_KEY, 'token-status'];
}

/** GET statut du lien d'activation (Envoyé/Expiré/Activé) des contacts du SIRET en session. */
export function useSupplierTeamTokenStatus(): UseQueryResult<
  readonly ContactTokenStatusDto[],
  Error
> {
  const client: SupplierTeamClient = useSupplierTeamClient();
  return useQuery<readonly ContactTokenStatusDto[], Error>({
    queryKey: supplierTeamTokenStatusKey(),
    queryFn: () => client.listTokenStatus(),
  });
}

/**
 * POST renvoi du lien d'activation à un contact (par contactId). On invalide le statut sur
 * `onSettled` : le succès ET le 409 « déjà activé » doivent refléter l'état réel (refetch).
 */
export function useResendSupplierActivation(): UseMutationResult<void, Error, string> {
  const client: SupplierTeamClient = useSupplierTeamClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (contactId: string) => client.resendActivation(contactId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: supplierTeamTokenStatusKey() });
    },
  });
}

/** POST invitation d'un collaborateur (invalide l'annuaire au succès). */
export function useInviteCollaborator(): UseMutationResult<
  InviteCollaboratorResultDto,
  Error,
  InviteCollaboratorInput
> {
  const client: SupplierTeamClient = useSupplierTeamClient();
  const queryClient = useQueryClient();
  return useMutation<InviteCollaboratorResultDto, Error, InviteCollaboratorInput>({
    mutationFn: (input) => client.inviteCollaborator(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supplierTeamKey() });
    },
  });
}

/** POST révocation d'un collaborateur PAR le MASTER (invalide l'annuaire au succès). */
export function useRevokeCollaborator(): UseMutationResult<
  void,
  Error,
  RevokeCollaboratorInput
> {
  const client: SupplierTeamClient = useSupplierTeamClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, RevokeCollaboratorInput>({
    mutationFn: (input) => client.revokeCollaborator(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supplierTeamKey() });
    },
  });
}
